import { lazy } from "react";
import { useResolve } from "react-jpex";
import { Navigate, Route, Routes } from "react-router";

import { AuthenticatedGuard } from "components/AuthenticatedGuard/AuthenticatedGuard";
import {
  ACTIVITY,
  COLLECTION,
  DASHBOARD,
  DATA_ROOM,
  LOCATION,
  LOGBOOK,
  NOT_FOUND,
  REMINDERS,
  SERVICE_REQUEST,
  VAULT,
  VEHICLE,
  WISHLIST,
} from "constants/routes";
import { useCheckQuotas } from "hooks/useCheckQuotas";
import ServiceRequestRoutes from "pages/service-request/ServiceRequestRoutes";
import { AuthenticationService } from "typings/AuthenticationService";
import { hasExternalPartner } from "typings/Organization";
import { Role } from "typings/Role";
import { dataRoomEnabled, garageVaultEnabled } from "utils/featureFlags";

const CollectorContentLayout = lazy(
  () => import("pages/CollectorContentLayout"),
);
const ActivityRoutes = lazy(() => import("pages/activity/ActivityRoutes"));
const CollectionRoutes = lazy(
  () => import("pages/collection/CollectionRoutes"),
);
const DataRoomRoutes = lazy(() => import("pages/data-room/DataRoomRoutes"));
const LocationRoutes = lazy(() => import("pages/location/LocationRoutes"));
const LogbookRoutes = lazy(() => import("pages/logbook/LogbookRoutes"));
const RemindersRoutes = lazy(() => import("pages/reminders/RemindersRoutes"));
const VehicleRoutes = lazy(() => import("pages/vehicle/VehicleRoutes"));
const VaultRoutes = lazy(() => import("pages/vault/VaultRoutes"));
const DashboardPage = lazy(() => import("pages/dashboard/DashboardPage"));
const WishlistPage = lazy(() => import("pages/wishlist/WishlistPage"));

const ROLES = [
  Role.COLLECTOR,
  Role.CONCIERGE,
  Role.ORGANIZATION_MANAGER,
  Role.DEALER,
];

export default function CollectorContentRoutes() {
  const authService = useResolve<AuthenticationService>(
    "AuthenticationService",
  );

  const { canEdit } = useCheckQuotas();

  return (
    <Routes>
      <Route element={<AuthenticatedGuard roles={ROLES} />}>
        <Route element={<CollectorContentLayout />}>
          <Route element={<DashboardPage />} path={DASHBOARD} />

          {dataRoomEnabled && (
            <Route element={<DataRoomRoutes />} path={`${DATA_ROOM}/*`} />
          )}
        </Route>

        {authService.isCollector() && (
          <>
            <Route element={<ActivityRoutes />} path={`${ACTIVITY}/*`} />

            <Route element={<CollectionRoutes />} path={`${COLLECTION}/*`} />

            <Route element={<LocationRoutes />} path={`${LOCATION}/*`} />
          </>
        )}

        <Route element={<LogbookRoutes />} path={`${LOGBOOK}/*`} />

        {(authService.isCollector() ||
          authService.hasOrganizationPrivileges()) &&
          hasExternalPartner(authService.getOrganizationsIds()) && (
            <Route element={<CollectorContentLayout />} path={SERVICE_REQUEST}>
              <Route
                element={<ServiceRequestRoutes />}
                index
                path={`${SERVICE_REQUEST}/*`}
              />
            </Route>
          )}

        {(authService.isCollector() ||
          authService.hasOrganizationPrivileges()) && (
          <Route element={<RemindersRoutes />} path={`${REMINDERS}/*`} />
        )}

        {authService.isCollector() && (
          <Route element={<CollectorContentLayout />}>
            <Route
              element={<WishlistPage readOnly={!canEdit} />}
              path={`${WISHLIST}/:wishlistId?`}
            />
          </Route>
        )}

        {garageVaultEnabled && (
          <Route element={<VaultRoutes />} path={`${VAULT}/*`} />
        )}

        <Route element={<VehicleRoutes />} path={`${VEHICLE}/*`} />

        <Route element={<Navigate to={VEHICLE} />} index />

        <Route element={<Navigate replace to={NOT_FOUND} />} path="/*" />
      </Route>
    </Routes>
  );
}
